import React, { useState } from 'react';
import { Box, FormControl, FormLabel, Stack, TextField, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import { Popup } from 'components/common/popup/Popup';
import { customRequestAPI } from 'api/api';

interface RemoveKeptLinkCellProps {
    /** POST endpoint: /data/editor/kg-profile/models/{modelObjectUid}/kept-links/{linkageId}/remove */
    href: string;
    measureName: string;
    modelName: string;
    /** The field's current kind, already translated by the grid transformer. */
    fieldKind: string;
    reloadGridData?: () => void;
    dataTestCell?: string;
}

const TYPED_ERRORS = [
    'model_update_in_progress',
    'link_not_found',
    'no_field_entry',
    'link_not_kept',
    'term_not_editable',
    'model_not_found',
];

/**
 * MI-30557: Remove link on a kept row of the glossary term's Knowledge Graph Models & Domains grid. The
 * dialog needs a reason; the back end's typed refusal stays inside the dialog and the row stays.
 */
export default function RemoveKeptLinkCell({
    href,
    measureName,
    modelName,
    fieldKind,
    reloadGridData,
    dataTestCell = 'glossary_kept_link_remove',
}: RemoveKeptLinkCellProps) {
    const { t } = useBundleTranslation('app/editor/glossary');
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState(false);
    const trimmed = reason.trim();

    const messageFor = (code: unknown) =>
        t(TYPED_ERRORS.includes(String(code)) ? `remove_link_error_${code}` : 'remove_link_error_generic');

    const openDialog = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setReason('');
        setError(null);
        setOpen(true);
    };

    const confirm = async () => {
        if ('' === trimmed || pending) return;
        setPending(true);
        setError(null);
        try {
            const response = await customRequestAPI.post(href, { reason: trimmed });
            if (response?.data?.ok) {
                setOpen(false);
                reloadGridData?.();
                return;
            }
            setError(messageFor(response?.data?.error));
        } catch (e: any) {
            setError(messageFor(e?.response?.data?.error));
        } finally {
            setPending(false);
        }
    };

    return (
        <>
            <Box component="a" onClick={openDialog} sx={{ cursor: 'pointer' }} data-test={dataTestCell}>
                {t('remove_link')}
            </Box>
            <Popup
                open={open}
                settings={{
                    title: t('remove_link_title', { measure: measureName }),
                    needTranslation: false,
                    textOK: t('remove_link_confirm'),
                    dataTestOkButton: 'glossary_kept_link_remove_ok',
                    dataTestCancelButton: 'glossary_kept_link_remove_cancel',
                }}
                onHide={() => setOpen(false)}
                onConfirm={confirm}
                okDisabled={pending || '' === trimmed}
                maxWidth="popupSm"
            >
                <Stack direction="column" spacing={2}>
                    <Typography data-test="glossary_kept_link_remove_note" sx={{ fontSize: '12px' }}>
                        {t('remove_link_note', { measure: measureName, model: modelName, kind: fieldKind })}
                    </Typography>
                    <FormControl fullWidth>
                        <FormLabel htmlFor="glossary_kept_link_reason_input">{t('remove_link_reason_label')}</FormLabel>
                        <TextField
                            id="glossary_kept_link_reason_input"
                            data-test="glossary_kept_link_reason"
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                setError(null);
                            }}
                            placeholder={t('remove_link_reason_placeholder')}
                            fullWidth
                            multiline
                            minRows={3}
                            autoFocus
                            disabled={pending}
                        />
                    </FormControl>
                    {error && (
                        <Typography
                            data-test="glossary_kept_link_remove_error"
                            sx={{ fontSize: '12px', color: 'error.main' }}
                        >
                            {error}
                        </Typography>
                    )}
                </Stack>
            </Popup>
        </>
    );
}
